import { rem } from "@certn/cascadia/utils";
import { CascadiaSpacing } from "@certn/cascadia-tokens";
import { css } from "@emotion/css";
import { ReactNode, useState, useCallback, useMemo } from "react";
import { mergeProps } from "react-aria";
import { Tabs as AriaTabs, TabsProps } from "react-aria-components";

import { TabBadgesContext } from "./context";

interface Props extends TabsProps {
  gap?: CascadiaSpacing;
  testID: string;
}

export function Tabs(props: Props) {
  const { gap = "space-5", ...tabsProps } = props;

  const [badges, setBadges] = useState<Record<string, ReactNode>>({});

  const setBadge = useCallback((id: string, badge?: ReactNode) => {
    setBadges((curr) => ({
      ...curr,
      [id]: badge,
    }));
  }, []);

  const contextValue = useMemo(
    () => ({ badges, setBadge }),
    [badges, setBadge]
  );

  return (
    <TabBadgesContext.Provider value={contextValue}>
      <AriaTabs
        {...mergeProps(tabsProps, {
          className: styles.tabs,
          style: { gap: rem(gap) },
        })}
        data-testid={props.testID}
      />
    </TabBadgesContext.Provider>
  );
}

const styles = {
  tabs: css({
    display: "flex",
    gap: "var(--space-5)",

    "&[data-orientation=horizontal]": {
      flexDirection: "column",

      ".react-aria-Tab": {
        "&[data-hovered]": {
          borderBottomWidth: "1px",
          borderBottomStyle: "solid",
          paddingBottom: `calc(var(--space-3) - 1px)`,
        },

        "&[data-selected]": {
          borderBottomWidth: "3px",
          borderBottomStyle: "solid",
          borderTopLeftRadius: "var(--border-radius-1)",
          borderTopRightRadius: "var(--border-radius-1)",
          paddingBottom: `calc(var(--space-3) - 3px)`,
        },
      },
    },

    "&[data-orientation=vertical]": {
      flexDirection: "row",

      ".react-aria-Tab": {
        "&[data-selected]": {
          borderLeftWidth: "3px",
          borderLeftStyle: "solid",
          paddingLeft: `calc(var(--space-4) - 3px)`,
        },
      },

      ".react-aria-TabPanel": {
        flexGrow: 1,
      },
    },
  }),
};

export { Tab } from "./assets/Tab";
export { TabList } from "./assets/TabList";
export { TabPanel } from "./assets/TabPanel";
export { TabBadgesContext } from "./context";
